/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {ENABLE_HELP_WITH_TIMESLOTS, eventCategories, LOCALE, SELECT_CUSTOM_TIMESLOTS_ALLOWED} from "helpers/config";
import ImportantArriveTime from "pages/Front/Booking/components/ImportantArriveTime";
import NeedHelpWithTimeslots from "pages/Front/Booking/components/BookingTimeslots/NeedHelpWithTimeslots";
import {PackageCategory} from "types/enums/PackageCategory";
import clsx from "clsx";
import {ClockIcon} from "@heroicons/react/24/outline";
import Skeleton from "react-loading-skeleton";
import {Timeslot} from "types/entities/Timeslot";
import BookingTimeslot from "pages/Front/Booking/components/BookingTimeslots/BookingTimeslot";
import {fakeTimeslotMinute, getPackageRespectsTimeslotSize, useClearUpsells} from "utils/packageUtils";
import AvailableDateButton from "pages/Front/Booking/components/BookingTimeslots/AvailableDateButton";
import Dates from "utils/Dates";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {BookingSteps} from "types/enums/BookingSteps";
import Loader from "components/Loader";
import useCustomTimeslotsNew from "utils/hooks/useCustomTimeslotsNew";
import useStepsHelper from "utils/hooks/useStepsHelper";
import BookingFooterSteps from "pages/Front/Booking/components/BookingFooterSteps";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import BookingCard from "pages/Front/Booking/components/BookingCard";

export interface BookingTimeslotsNewParams {}

interface BookingTimeslotsNewProps extends RouteComponentProps<BookingTimeslotsNewParams> {
    selectTimeslotHandler: (bookingStep: BookingSteps) => void;
}

const BookingTimeslotsNew = (props: BookingTimeslotsNewProps) => {
    const {t} = useTranslation();
    const bookingTimeslots = useTypedSelector(state => state.bookingState.timeslots);
    const sessionLangauge = useTypedSelector(state => state.sessionState.language);
    const bookingProcess = useTypedSelector(state => state.bookingState.bookingProcess);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const selectedPackageCategory = useTypedSelector(state => state.bookingState.bookingProcess.filter.category);
    const customSelectedTimeslotState = useTypedSelector(state => state.bookingState.bookingProcess.customTimeslots);
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);
    const [lastBookingTimeslotSize, setLastBookingTimeslotSize] = useState(16);
    const [skeletonArray, setSkeletonArray] = useState<any[]>([]);
    //the timeslot that is selected in the cart or by automatic selection
    const packageRespectsTimeslotSize = selectedPackage && getPackageRespectsTimeslotSize(selectedPackage);

    const lastMidnight = new Date();
    lastMidnight.setHours(0, 0, 0, 0);

    const clearPackagesHook = useClearUpsells(bookingProcess);

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);
    const blockedClass = "bg-gray-600 pointer-events-none opacity-50";
    console.log(selectedPackageCategory);
    const {
        availableDates,
        currentDateIndex,
        customSelectedTimeslots,
        firstPossibleSlotIndex,
        isLoadingTimeslots,
        lastPossibleSlotIndex,
        possibleTimeslots,
        resetCustomSelectedTimeslotHandler,
        selectCustomSlotsEnabled,
        selectedTimeslot,
        setSelectCustomSlotsEnabledHandler,
        setSelectedTimeslotHandler,
        setStepsEnabledHandler,
        stepsEnabled,
        switchDate,
        timeslotsAvailable,
        bookWithAutomaticTimeslot,
    } = useCustomTimeslotsNew(props.selectTimeslotHandler, props.history, [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots]);

    useEffect(() => {
        clearPackagesHook.clearInvalidUpsells();
    }, [selectedUpsells]);

    const stepsHandler = useStepsHelper(selectedPackage, stepsEnabled, setStepsEnabledHandler, setSelectedTimeslotHandler, resetCustomSelectedTimeslotHandler);

    const onNextStepHandle = () => {
        selectedTimeslot && bookWithAutomaticTimeslot(selectedTimeslot);
    };

    useEffect(() => {
        setSkeletonArray(new Array(lastBookingTimeslotSize).fill(""));
    }, [lastBookingTimeslotSize]);

    useEffect(() => {
        setLastBookingTimeslotSize(timeslotsAvailable.length);
    }, [timeslotsAvailable]);

    if (!bookingTimeslots) {
        return <Loader />;
    }

    return (
        <div
            className={clsx(
                // "BookingTimeslotsOnly -mb-4 md:mb-0",
                "BookingTimeslotsOnly md:mb-0 -mb-4",
                "m-auto flex flex-col w-full h-full flex-grow justify-between md:justify-start items-center text-center select-none "
            )}
        >
            {timeslotsAvailable && timeslotsAvailable.length > 0 && stepsEnabled && <stepsHandler.StepsComponent stepsEnabled={stepsEnabled} />}
            <div
                className={clsx(
                    // "BookingTimeslotHeader grid grid-cols-3 sticky top-0 z-40 bg-gray-100 justify-between mb-4 -mx-2 lg:mb-4 lg:-m-2 w-full pt-2",
                    "BookingTimeslotHeader grid grid-cols-3 sticky -top-2 z-40 bg-white justify-between w-full",
                    // "before:content-[''] before:block before:absolute before:top-0 before:left-[-10px] before:w-2.5 before:h-full before:bg-gray-100 after:content-[''] ",
                    // "after:block after:absolute after:top-0 after:right-[-10px] after:w-2.5 after:h-full after:bg-gray-100"
                    "mb-6 rounded-[10px] drop-shadow pt-0 px-4"
                )}
            >
                {/*<div className={"BookingTimeslotHeader col-span-3 flex flex-col items-center justify-center w-full"}>*/}
                {/*    <BookingCardTitle className={"font-medium text-base text-gray-900 text-center sm:text-left"} title={t("Choose timeslot")} />*/}
                {/*    {selectCustomSlotsEnabled && customSelectedTimeslots.length >= 0 && (*/}
                {/*        <div className={"flex flex-row"}>*/}
                {/*            <span>{customSelectedTimeslots.length}</span>*/}
                {/*            <span>/</span>*/}
                {/*            <span>{selectedPackage?.typical_timeslot_count}</span>*/}
                {/*        </div>*/}
                {/*    )}*/}
                {/*</div>*/}
                <AvailableDateButton
                    availableDates={availableDates}
                    classNameButton={
                        "border !py-1 !pr-1.5 !pl-6 rounded-md !h-auto bg-arbo-orange hover:bg-white border-arbo-orange hover:border-arbo-orange text-white font-medium shadow-sm hover:no-underline hover:!text-black !duration-150 hover:!duration-150"
                    }
                    classNameIcon={"left-0.5"}
                    classNameWrapper={"self-center"}
                    currentDateIndex={currentDateIndex}
                    direction={"prev"}
                    onClick={date => switchDate(date)}
                />
                <div className={"relative flex flex-col items-center justify-end w-full text-base 2xl:text-lg leading-[1] py-2"}>
                    <span className={"font-bold"}>{Dates.intlDateFormat(new Date(filter.date).getTime(), {dateStyle: "short"})}</span>
                    <span className={"text-sm leading-[1]"}>
                        {Dates.intlDateFormat(new Date(filter.date).getTime(), {weekday: "long"}, sessionLangauge ? sessionLangauge.locale : LOCALE)}&nbsp;
                    </span>
                </div>
                <AvailableDateButton
                    availableDates={availableDates}
                    classNameButton={
                        "border !py-1 !pr-6 !pl-1.5 rounded-md !h-auto bg-arbo-orange hover:bg-white border-arbo-orange hover:border-arbo-orange text-white font-medium shadow-sm hover:no-underline hover:!text-black !duration-150 hover:!duration-150"
                    }
                    classNameIcon={"right-0.5"}
                    classNameWrapper={"self-center"}
                    currentDateIndex={currentDateIndex}
                    direction={"next"}
                    onClick={date => switchDate(date)}
                />
            </div>

            <BookingCard className={"w-full !px-2 xl:!px-3"}>
                <div className={"relative grow md:grow-0 justify-start flex flex-col w-full overflow-y-auto"}>
                    <div
                        ref={scrollRefTimeslots}
                        className={
                            "timeslots relative px-1 grid w-full grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 justify-center items-center gap-4 py-1 mx-auto"
                        }
                        id="BookingTimeslots"
                    >
                        {isLoadingTimeslots &&
                            skeletonArray.map((i, index) => {
                                return (
                                    <div
                                        key={index}
                                        className={
                                            "w-full min-w-[88px] h-[50px] sm:h-[62px] border border-solid border-gray-800/20  overflow-hidden rounded-lg flex leading-[1]"
                                        }
                                    >
                                        <Skeleton containerClassName={"flex-1"} height={"100%"} width={"100%"} />
                                    </div>
                                );
                            })}
                        {!isLoadingTimeslots &&
                            timeslotsAvailable.map((timeslot: Timeslot, index: number) => {
                                const possibleTimeslotIndex = possibleTimeslots.findIndex(possible => possible.id === timeslot.id);
                                const customTimeslotIndex = customSelectedTimeslots
                                    .sort((a, b) => (a.id > b.id ? 1 : -1))
                                    .findIndex(customTimeslot => customTimeslot.id === timeslot.id);
                                return (
                                    <BookingTimeslot
                                        key={index}
                                        customTimeslotIndex={customTimeslotIndex}
                                        fakeTimeslotMinute={fakeTimeslotMinute(selectedPackage)}
                                        // fakeTimeslotMinute={false}
                                        firstPossibleSlotIndex={firstPossibleSlotIndex}
                                        index={index}
                                        lastPossibleSlotIndex={lastPossibleSlotIndex}
                                        packageRespectsTimeslotSize={packageRespectsTimeslotSize}
                                        // packageRespectsTimeslotSize={false}
                                        possibleTimeslotIndex={possibleTimeslotIndex}
                                        possibleTimeslotsLength={possibleTimeslots.length}
                                        selectedTimeslot={selectedTimeslot}
                                        showTimeslotOccupancy={false}
                                        showEventHoursCategory={!selectedPackageCategory ? false : eventCategories.includes(selectedPackageCategory)}
                                        timeslot={timeslot}
                                        onClick={() => setSelectedTimeslotHandler(timeslot, selectCustomSlotsEnabled)}
                                    />
                                );
                            })}
                        {!isLoadingTimeslots && timeslotsAvailable.length === 0 && (
                            <div className={"w-full flex flex-col items-center justify-center col-span-3 sm:col-span-4 md:col-span-6"}>
                                <div className={"p-3 xl:p-4 rounded-md shadow-md bg-white text-cta-800 max-w-[326px] lg:max-w-[640px]"}>
                                    <h3 className={"text-lg font-medium"}>
                                        {t("Sorry")}, {t("completely booked out on this day")}
                                    </h3>
                                    <span>{t("Please choose another date")}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </BookingCard>

            <div
                className={"flex flex-col justify-center items-center h-auto mt-0 w-full bg-gray-100 z-40 sticky inset-x-0 bottom-0 md:relative md:bottom-auto"}
            >
                {ENABLE_HELP_WITH_TIMESLOTS && timeslotsAvailable && timeslotsAvailable.length > 0 && (
                    <>
                        <ImportantArriveTime selectedUpsells={selectedUpsells} />
                        <NeedHelpWithTimeslots onClick={() => setStepsEnabledHandler(true)} />
                    </>
                )}
                <section
                    className={clsx(
                        "flex flex-row justify-center items-center space-x-2 w-full md:mt-6 py-3 md:pt-0 md:p-0",
                        "before:content-[''] before:block before:absolute before:top-0 before:left-[-10px] before:w-2.5 before:h-full before:bg-gray-100 md:before:hidden after:content-[''] after:block after:absolute after:top-0 after:right-[-10px] after:w-2.5 after:h-full after:bg-gray-100 md:after:hidden"
                    )}
                >
                    <BookingFooterSteps nextStepDisabled={!selectedTimeslot}>
                        {SELECT_CUSTOM_TIMESLOTS_ALLOWED &&
                            selectedPackage &&
                            selectedPackage.typical_timeslot_count > 1 &&
                            selectedPackage.category !== PackageCategory.Event && (
                                <div className={"SelectCustomTimeslots grid justify-center items-center gap-4 sm:bottom-auto z-40"}>
                                    <div ref={scrollRefCustomTimeslots} className={"flex flex-row p-3 sm:p-4 w-full inset-x-0"}>
                                        <button
                                            className={clsx(
                                                selectedTimeslot === null && customSelectedTimeslots.length === 0 && blockedClass,
                                                "bg-primary-800 text-primary-100 cursor-pointer hover:no-underline text-xs sm:text-xl flex-grow text-center px-3 py-2 sm:px-5 sm:py-3 flex flex-row justify-center rounded-lg items-center"
                                            )}
                                            disabled={selectedTimeslot === null && customSelectedTimeslots.length === 0}
                                            type={"button"}
                                            onClick={() => setSelectCustomSlotsEnabledHandler(!selectCustomSlotsEnabled)}
                                        >
                                            <div className={"flex flex-row items-center space-x-1 sm:space-x-2"}>
                                                <ClockIcon className={"w-3 h-3 shrink-0"} />
                                                <span className={"leading-4 sm:leading-7"}>
                                                    {selectCustomSlotsEnabled ? t("Dont use my slots") : t("Select my next slots")}
                                                </span>
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            )}

                        <div className={"ConfirmTimeslotButton grid justify-center items-center gap-4 sm:bottom-auto z-40"}>
                            <div ref={scrollRefConfirmTimeslots} className={"flex flex-row w-full inset-x-0"}>
                                <ButtonSubmit
                                    className={clsx(
                                        "inline-flex items-center px-4 sm:px-3 py-3 w-auto rounded-md sm:min-w-[210px] !font-medium !text-base sm:!text-lg sm:!leading-none",
                                        !selectedTimeslot && "border-gray-400 !cursor-not-allowed"
                                    )}
                                    disabled={!selectedTimeslot}
                                    icon={"ticket"}
                                    id="ButtonSubmitUpsells"
                                    label={t("Continue with selected time")}
                                    onClick={onNextStepHandle}
                                />
                            </div>
                        </div>
                    </BookingFooterSteps>
                </section>
            </div>
        </div>
    );
};

export default withRouter(BookingTimeslotsNew);
